import { z } from 'zod';

import { zodAddressSchema } from '@vestigas/data';

import { SUPPLIER_REQUEST_STATE, VOLUME_BUCKET } from '../constants';
import {
  zodCompanyRefSchema,
  zodResolvedCompanyRefSchema,
  zodSubmittedSupplierInfoSchema,
} from '../validations';

/**
 * Zod schema for the POST /suppliers/requests request body (SupplierRequestCreateBody).
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/post_unlisted_supplier_suppliers_requests_post
 */
export const zodRequestNewSupplierPayloadSchema = z.object({
  address: zodAddressSchema.nullish(),
  contactEmail: z.email().nullish(),
  dlnCountRange: z.enum(VOLUME_BUCKET).nullish(),
  gln: z.string().trim().max(13).nullish(),
  hra: z.string().trim().max(64).nullish(), // The HRA and the HRB each carry their own register court; a company can be registered in both registers at different courts.
  hraRegisterCourt: z.string().trim().max(80).nullish(),
  hrb: z.string().trim().max(64).nullish(),
  invCountRange: z.enum(VOLUME_BUCKET).nullish(),
  name: z.string().trim().min(1).max(500),
  note: z.string().trim().nullish(),
  recipientCompanyId: z.uuid(),
  registerCourt: z.string().trim().max(80).nullish(),
  revenue: z.union([z.number(), z.string()]).nullish(),
  vatId: z.string().max(64).nullish(),
});

/**
 * Zod schema for the POST /suppliers/requests 201 response (SupplierRequestDetailResponse).
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/post_unlisted_supplier_suppliers_requests_post
 */
export const zodRequestNewSupplierResponseSchema = z.object({
  createdAt: z.iso.datetime({ offset: true }),
  createdByUserId: z.uuid(),
  id: z.uuid(),
  potentialDuplicateCompanyIds: z.array(z.uuid()).prefault([]),
  rejectionReason: z.string().nullish(),
  requestingCompany: zodCompanyRefSchema,
  requestingCompanyAccount: zodCompanyRefSchema,
  resolvedCompany: zodResolvedCompanyRefSchema.nullish(),
  state: z.enum(SUPPLIER_REQUEST_STATE),
  submitted: zodSubmittedSupplierInfoSchema,
  validatedAt: z.iso.datetime({ offset: true }).nullish(),
  validatedByUserId: z.uuid().nullish(),
});
